/**
 * Private Lessons calendar data layer.
 * Uses API: POST /api/appointments/scroll + POST /api/appointments/bookings/bulk.
 */

import { fetchAppointmentBookingsByIds } from "@/features/appointments/utils/fetch-appointment-bookings-bulk";
import { createAuthenticatedApi } from "@/lib/utils";
import { formatYmdLocal, parseAppointmentApiDateTime } from "@/lib/utils/date";
import {
  formatStudioHHmm,
  formatYmdInTimeZone,
  getRuntimeTenantTimeZone,
} from "@/lib/utils/date/studio-clock";
import { PrivatesAppointmentsApi } from "@/openapi/generated";

function ymdLocal(d: Date): string {
  return formatYmdLocal(d);
}

/** Cancelled / rejected only - other statuses (and unknown) count for the list. */
function isBookingExcludedFromPicker(status: unknown): boolean {
  if (status == null) return false;
  const s = String(status).toLowerCase();
  return s.includes("cancel") || s.includes("reject");
}

export interface PrivateLessonEntry {
  id: string;
  studentId: number;
  studentName: string;
  teacherId: number;
  teacherName: string;
  date: string; // YYYY-MM-DD
  startTime: string;
  endTime: string;
  notes: string | null;
  appointmentId: number;
  appointmentName: string | null;
  bookingId: number;
  locationName: string | null;
  classroomName: string | null;
  participantCount: number | null;
  instructorNotes: string | null;
  status: string | null;
  /**
   * Synthetic row when the appointment has no real bookings in the requested date range
   * (date/time are anchor placeholders for the calendar, not a scheduled lesson).
   */
  isPlaceholder: boolean;
}

export interface PrivateLessonsFilters {
  studentId?: number | null;
  teacherId?: number | null;
  startDate?: string | null;
  endDate?: string | null;
}

const MAX_APPOINTMENTS_TO_FETCH = 60;

/**
 * Fetch private lesson calendar entries from API (scroll + bookings per appointment).
 * GET /api/appointments/{id} returns appointment metadata only; GET /api/appointments/{id}/bookings returns the actual scheduled lessons (bookings).
 */
export async function getPrivateLessonEntriesFromApi(
  accessToken: string,
  filters: PrivateLessonsFilters,
  timeZone: string | null | undefined = getRuntimeTenantTimeZone(),
): Promise<PrivateLessonEntry[]> {
  const service = createAuthenticatedApi(PrivatesAppointmentsApi, accessToken);

  const scrollResult = await service.apiAppointmentsScrollPost({
    appointmentScrollQuery: {
      locationId: null,
      teacherId: filters.teacherId ?? null,
      includeOnlyOnline: null,
      date: null,
      searchQuery: null,
    },
    page: 1,
    pageSize: MAX_APPOINTMENTS_TO_FETCH,
  });

  const items = scrollResult?.items ?? [];
  const bookingsByAppointmentId = await fetchAppointmentBookingsByIds(
    accessToken,
    items.map((appointment) => Number(appointment.appointmentId)),
  );

  const entries: PrivateLessonEntry[] = [];

  for (const appointment of items) {
    if (
      filters.teacherId != null &&
      Number(appointment.teacher?.teacherId) !== Number(filters.teacherId)
    ) {
      continue;
    }
    try {
      const bookings =
        bookingsByAppointmentId.get(Number(appointment.appointmentId)) ?? [];
      const teacherName = appointment.teacher
        ? `${appointment.teacher.firstName ?? ""} ${appointment.teacher.lastName ?? ""}`.trim() ||
          "-"
        : "-";
      const teacherId = Number(appointment.teacher?.teacherId ?? 0);

      let addedFromBookings = 0;
      for (const b of bookings ?? []) {
        if (isBookingExcludedFromPicker(b.status)) {
          continue;
        }
        const start =
          parseAppointmentApiDateTime(b.startTime) ?? new Date(b.startTime);
        const end =
          parseAppointmentApiDateTime(b.endTime) ?? new Date(b.endTime);
        const dateStr = formatYmdInTimeZone(start, timeZone);
        if (filters.startDate && dateStr < filters.startDate) {
          continue;
        }
        if (filters.endDate && dateStr > filters.endDate) {
          continue;
        }
        if (
          filters.studentId != null &&
          b.student?.studentId !== filters.studentId
        ) {
          continue;
        }

        const studentName = b.student
          ? `${b.student.firstName ?? ""} ${b.student.lastName ?? ""}`.trim() ||
            "-"
          : "-";
        const studentId = Number(b.student?.studentId ?? 0);

        const locationName =
          b.locationName ?? b.classRoom?.location?.name ?? null;
        const classroomName = b.classRoom?.name ?? null;

        addedFromBookings++;
        entries.push({
          id: `booking-${b.bookingId}`,
          studentId,
          studentName,
          teacherId,
          teacherName,
          date: dateStr,
          startTime: formatStudioHHmm(start, timeZone),
          endTime: formatStudioHHmm(end, timeZone),
          notes: b.reason ?? null,
          appointmentId: Number(appointment.appointmentId),
          appointmentName: appointment.name ?? null,
          bookingId: Number(b.bookingId),
          locationName,
          classroomName,
          participantCount:
            b.participantCount == null ? null : Number(b.participantCount),
          instructorNotes: b.instructorNotes?.trim() || null,
          status: b.status ?? null,
          isPlaceholder: false,
        });
      }

      if (addedFromBookings === 0) {
        let anchor = ymdLocal(new Date());
        if (filters.startDate && anchor < filters.startDate) {
          anchor = filters.startDate;
        }
        if (filters.endDate && anchor > filters.endDate) {
          anchor = filters.endDate;
        }
        entries.push({
          id: `appt-${appointment.appointmentId}`,
          studentId: 0,
          studentName: "",
          teacherId,
          teacherName,
          date: anchor,
          startTime: "09:00",
          endTime: "10:00",
          notes: null,
          appointmentId: Number(appointment.appointmentId),
          appointmentName: appointment.name ?? null,
          bookingId: 0,
          locationName: null,
          classroomName: null,
          participantCount: null,
          instructorNotes: null,
          status: null,
          isPlaceholder: true,
        });
      }
    } catch {
      // skip appointment if bookings processing fails
    }
  }

  entries.sort((a, b) => {
    const d = a.date.localeCompare(b.date);
    if (d !== 0) return d;
    return a.startTime.localeCompare(b.startTime);
  });

  return entries;
}

export interface StaffAppointmentRow {
  appointmentId: number;
  appointmentName: string | null;
  teacherId: number;
  teacherName: string;
  studentId: number;
  studentName: string;
  bookingId: number;
  startTime: Date;
  endTime: Date;
  status: string;
  reason: string | null;
  duration: string | null;
  locationName: string | null;
  classroomName: string | null;
  participantCount: number | null;
  instructorNotes: string | null;
}

/**
 * Fetch all private lesson bookings as flat rows for staff table (scroll + bookings per appointment).
 */
export async function getStaffAppointmentRowsFromApi(
  accessToken: string,
): Promise<StaffAppointmentRow[]> {
  const service = createAuthenticatedApi(PrivatesAppointmentsApi, accessToken);

  const scrollResult = await service.apiAppointmentsScrollPost({
    appointmentScrollQuery: {
      locationId: null,
      teacherId: null,
      includeOnlyOnline: null,
      date: null,
      searchQuery: null,
    },
    page: 1,
    pageSize: MAX_APPOINTMENTS_TO_FETCH,
  });
  const items = scrollResult?.items ?? [];
  const bookingsByAppointmentId = await fetchAppointmentBookingsByIds(
    accessToken,
    items.map((appointment) => Number(appointment.appointmentId)),
  );
  const rows: StaffAppointmentRow[] = [];

  for (const appointment of items) {
    try {
      const bookings =
        bookingsByAppointmentId.get(Number(appointment.appointmentId)) ?? [];
      const teacherName = appointment.teacher
        ? `${appointment.teacher.firstName ?? ""} ${appointment.teacher.lastName ?? ""}`.trim() ||
          "-"
        : "-";
      const teacherId = Number(appointment.teacher?.teacherId ?? 0);

      for (const b of bookings ?? []) {
        const start = new Date(b.startTime);
        const end = new Date(b.endTime);
        const studentName = b.student
          ? `${b.student.firstName ?? ""} ${b.student.lastName ?? ""}`.trim() ||
            "-"
          : "-";
        const studentId = Number(b.student?.studentId ?? 0);
        const locationName =
          b.locationName ?? b.classRoom?.location?.name ?? null;
        const classroomName = b.classRoom?.name ?? null;

        rows.push({
          appointmentId: Number(appointment.appointmentId),
          appointmentName: appointment.name ?? null,
          teacherId,
          teacherName,
          studentId,
          studentName,
          bookingId: Number(b.bookingId),
          startTime: start,
          endTime: end,
          status: b.status,
          reason: b.reason ?? null,
          duration: appointment.duration ?? null,
          locationName,
          classroomName,
          participantCount:
            b.participantCount == null ? null : Number(b.participantCount),
          instructorNotes: b.instructorNotes?.trim() || null,
        });
      }
    } catch {
      // skip
    }
  }

  rows.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  return rows;
}
